import type { ChecklistType } from '@arhs-fleetback/types/entities/checklists';
import type { AxiosResponse } from 'axios';
import { generatePath, matchPath } from 'react-router';

import { all, takeLatest, put, call, take, select, takeEvery } from 'redux-saga/effects';

import { restClient } from 'config/store/configureStore';
import * as GlobalActionTypes from 'constants/ActionTypes';
import {
  CHECKLIST_ACCESSORY,
  CHECKLIST_PARTS,
  DETAILS_PATH_BY_TYPE,
  GROUP_DETAILS_PATH_BY_TYPE,
  META_SOURCE,
} from 'constants/ChecklistConstants';
import { NEW_ENTITY_ID } from 'constants/EntityTitles';
import { PATH } from 'constants/RoutesConstants';
import { APPLICATION_IS_READY, FORCE_PAGE_RELOAD } from 'modules/app/actionTypes';
import { scopeToPage } from 'modules/app/sagas/utils';
import { applicationIsReady } from 'modules/app/selectors';
import type { SimpleRequestConfig } from 'modules/app/types';
import { simpleRequestsLoader } from 'modules/app/utils';

import { CHECKLIST_DETAILED_PATHS } from 'modules/checklists/constants';
import { getSelectedChecklistId } from 'modules/checklists/selectors/details';
import { getParams } from 'modules/checklists/selectors/selectExtrasIntervention';
import { selectDealer } from 'modules/dealers/actions';
import { isApiFailure } from 'modules/global/generic.guards';
import type { Action, ApiError, MetaAction, PutAndWaitResult } from 'modules/global/generic.types';
import { selectGroup } from 'modules/groups/actions';
import * as groupActionTypes from 'modules/groups/actionTypes';
import { getSelectedGroupId } from 'modules/groups/selectors';
import { LOCATION_CHANGE } from 'modules/router/actionTypes';
import { navigate } from 'modules/router/navigate';
import { FETCH_MODEL_SUCCESS } from 'modules/selfServices/actionTypes';

import { createNotification } from 'modules/toast-notifications/actions';

import { getSelectedDealerId } from 'selectors/dealerSelectors';

import { getCurrentLocation, getLocationState } from 'selectors/router';

import { fetchChecklistAdviceHistory, fetchChecklistAdviceHistorySuccess } from './actions/adviceHistory';
import * as dashboardActions from './actions/dashboard';
import * as actions from './actions/details';
import * as actionTypes from './actionTypes';
import type {
  CreateFromDefaultChecklistAction,
  DeleteChecklistAction,
  FetchChecklistAction,
  FetchChecklistsSelectAction,
  FetchDefaultChecklistAction,
  FetchDefaultChecklistSuccessAction,
  FetchSelectedChecklistsRequest,
  PatchAdviceAction,
  SearchChecklistsAction,
} from './types';
import type { DetailedChecklist } from './types/projections/detailed-checklist';

const TRANSLATION_NODE_BY_CHECKLIST_TYPE: Record<string, string> = {
  [CHECKLIST_ACCESSORY]: 'offers',
  [CHECKLIST_PARTS]: 'offers',
  DEFAULT: 'checklists',
};

const getNotificationMessageId = (type: ChecklistType, action: string, error = false): string => {
  const entity = TRANSLATION_NODE_BY_CHECKLIST_TYPE[type] ?? TRANSLATION_NODE_BY_CHECKLIST_TYPE.DEFAULT;
  const leaf = error ? 'failure' : 'success';
  return `page.config.dealer.${entity}.notification.${action}.${leaf}`;
};

function* updateChecklistSaga({ payload, meta = {} }: ReturnType<typeof actions.updateRequest>) {
  const { request } = payload;
  const checklistType = request.data.type;
  let message = getNotificationMessageId(checklistType, 'save');
  let type = 'success';
  try {
    const { data } = (yield call(restClient.request, request)) as AxiosResponse<DetailedChecklist>;
    yield put(actions.updateSuccess(data, meta));
  } catch (errorMessage) {
    yield put(actions.updateFailure(errorMessage as ApiError, meta));
    type = 'error';
    message = getNotificationMessageId(checklistType, 'save', true);
  }

  yield put(createNotification({ type, message }));
}

function* createChecklistSaga({ payload, meta = {} }: ReturnType<typeof actions.createRequest>) {
  const { request } = payload;
  const checklistType = request.data.type;
  let message = getNotificationMessageId(checklistType, 'create');
  let type = 'success';

  try {
    const { data: checklist } = (yield call(restClient.request, request)) as AxiosResponse<DetailedChecklist>;
    yield put(actions.createSuccess(checklist, meta));
    const pathByType = checklist.dealerId ? DETAILS_PATH_BY_TYPE : GROUP_DETAILS_PATH_BY_TYPE;
    const { path } = pathByType[checklistType] ?? pathByType.DEFAULT;
    yield call(navigate, generatePath(path, { checklistId: checklist.id }), { replace: true });
  } catch (errorMessage) {
    yield put(actions.createFailure(errorMessage as ApiError, meta));
    type = 'error';
    message = getNotificationMessageId(checklistType, 'create', true);
  }
  yield put(createNotification({ type, message }));
}

function* patchAdvice({ payload, meta = {} }: PatchAdviceAction) {
  const { request, checklistId, adviceId } = payload;

  try {
    const { data } = (yield call(restClient.request, request)) as AxiosResponse<Record<string, unknown>>;
    yield put(actions.patchAdviceSuccess({ checklistId, adviceId, ...data }, meta));
  } catch (error) {
    yield put(actions.patchAdviceFailure({ checklistId, adviceId }, error as ApiError, meta));
    const notification = {
      type: 'error',
      message: {
        id: 'http.error.message',
        defaultMessage: 'Oops! Something went wrong.',
      },
    };
    yield put(createNotification(notification));
  }
}

function* selectChecklist({ dealerId, groupId }: DetailedChecklist) {
  // Fetch the dealer if the dealerId is defined, the attached group will be fetched as well by dealer's saga
  if (dealerId) {
    const selectedDealerId: ReturnType<typeof getSelectedDealerId> = yield select(getSelectedDealerId);
    if (dealerId !== selectedDealerId) {
      yield put(selectDealer(dealerId));
      yield take([GlobalActionTypes.FETCH_DEALER_SUCCESS, GlobalActionTypes.FETCH_DEALER_FAILURE]);
    }
  } else if (groupId) {
    // Fetch the group if the groupId is defined and the dealerId is not, meaning a group checklist
    const selectedGroupId: ReturnType<typeof getSelectedGroupId> = yield select(getSelectedGroupId);
    if (groupId !== selectedGroupId) {
      yield put(selectGroup(groupId));
      yield take([groupActionTypes.FETCH_GROUP_SUCCESS, groupActionTypes.FETCH_GROUP_FAILURE]);
    }
  }
}

function* onDetailsPageWorker() {
  const checklistId: ReturnType<typeof getSelectedChecklistId> = yield select(getSelectedChecklistId);
  if (!checklistId) {
    return;
  }

  if (checklistId === NEW_ENTITY_ID) {
    const locationState: MetaAction | null = yield select(getLocationState);
    yield put(actions.detailedChecklistIsReady(true));
    yield put(actions.setIsNew(undefined, locationState ?? {}));
    return;
  }

  yield put(dashboardActions.fetchChecklist(checklistId));
  const result: PutAndWaitResult<DetailedChecklist> = yield take([actionTypes.FETCH_SUCCESS, actionTypes.FETCH_FAILURE]);

  if (!isApiFailure(result)) {
    yield call(selectChecklist, result.payload);
    yield put(actions.detailedChecklistIsReady(true));
  }
}

function* fetchChecklist({ payload, meta }: FetchChecklistAction) {
  const { request } = payload;
  try {
    const { data } = (yield call(restClient.request, request)) as AxiosResponse<DetailedChecklist>;
    yield put(dashboardActions.fetchChecklistSuccess(data, meta));
  } catch (message) {
    yield put(dashboardActions.fetchChecklistFailure(message as ApiError, meta));
  }
}

function* searchChecklists({ payload, meta }: SearchChecklistsAction) {
  const { request } = payload;

  try {
    const { data } = (yield call(restClient.request, request)) as AxiosResponse<
      ReturnType<typeof dashboardActions.searchChecklistsSuccess>['payload']
    >;
    yield put(dashboardActions.searchChecklistsSuccess(data, meta));
  } catch (error) {
    yield put(dashboardActions.searchChecklistsFailure(error as ApiError, meta));
  }
}

function* deleteChecklist({ payload, meta }: DeleteChecklistAction) {
  const { request, checklistId, type } = payload;
  try {
    yield call(restClient.request, request);
    yield put(
      createNotification({
        message: getNotificationMessageId(type, 'delete'),
        type: 'success',
      })
    );
    yield put(dashboardActions.deleteChecklistSuccess(checklistId, meta));
  } catch (error) {
    yield put(
      createNotification({
        message: getNotificationMessageId(type, 'delete', true),
        type: 'error',
      })
    );
    yield put(dashboardActions.deleteChecklistFailure(error as ApiError, meta));
  }
}

function* fetchChecklistsSelect({ payload, meta }: FetchChecklistsSelectAction) {
  const { request } = payload;
  try {
    const { data } = (yield call(restClient.request, request)) as AxiosResponse<
      ReturnType<typeof dashboardActions.searchChecklistsSuccess>['payload']
    >;
    const successPayload = { ...data, dealerId: request.params.dealerId };
    yield put(dashboardActions.fetchChecklistsSelectSuccess(successPayload, meta));
  } catch (error) {
    yield put(dashboardActions.fetchChecklistsSelectFailure(error as ApiError, meta));
  }
}

function* fetchDefaultChecklist({ payload, meta }: FetchDefaultChecklistAction) {
  const { request } = payload;
  try {
    const { data } = (yield call(restClient.request, request)) as AxiosResponse<DetailedChecklist>;
    yield put(dashboardActions.fetchDefaultChecklistSuccess(data, meta));
  } catch (error) {
    yield put(dashboardActions.fetchDefaultChecklistFailure(error as ApiError, meta));
  }
}

function* startDetailsApplication() {
  const location: ReturnType<typeof getCurrentLocation> = yield select(getCurrentLocation);
  const pathname = location ?? '';

  const paths = [
    PATH.CHECKLISTS_DETAILED,
    PATH.OFFERS_DETAILED,
    PATH.CHECKLISTS,
    PATH.OFFERS_DASHBOARD,
    PATH.GROUP_CHECKLISTS_CONFIG,
    PATH.GROUP_OFFERS,
  ];
  const path = paths.find(item => matchPath(item.path, pathname));

  if (path) {
    const isDetailsPath = path.path === PATH.CHECKLISTS_DETAILED.path || path.path === PATH.OFFERS_DETAILED.path;
    const isReady: ReturnType<typeof applicationIsReady> = yield select(applicationIsReady);
    if (!isReady) {
      yield take(APPLICATION_IS_READY);
    }

    if (!isDetailsPath) {
      yield put(dashboardActions.hideDuplicateModal());
    }
  }
}

function* fetchExtraCollection() {
  const requestParams: ReturnType<typeof getParams> = yield select(getParams);
  yield put(
    dashboardActions.searchExtras(
      { ...requestParams, page: 0, projection: 'page' },
      { alterable: false, source: META_SOURCE.EXTRAS_INTERVENTION_DETAILS }
    )
  );
}

function* createChecklistFromDefault({ payload }: CreateFromDefaultChecklistAction) {
  const { type, ...duplicateData } = payload;
  yield put(dashboardActions.fetchDefaultChecklist({ type }));

  const result = (yield take([
    actionTypes.FETCH_DEFAULT_CHECKLIST_SUCCESS,
    actionTypes.FETCH_DEFAULT_CHECKLIST_FAILURE,
  ])) as FetchDefaultChecklistSuccessAction;

  if (result.type === actionTypes.FETCH_DEFAULT_CHECKLIST_SUCCESS) {
    yield put(dashboardActions.duplicateChecklist({ id: result.payload.id, ...duplicateData }));
  } else {
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'page.config.dealer.checklists.notification.createFromDefault.failure',
          defaultMessage: "Can't create the checklist from the default one.",
        },
      })
    );
  }
}

function* duplicateChecklist({ payload }: ReturnType<typeof dashboardActions.duplicateChecklist>) {
  const { request } = payload;
  try {
    const { data } = (yield call(restClient.request, request)) as AxiosResponse<DetailedChecklist>;
    yield put(dashboardActions.duplicateChecklistSuccess(data));

    const pathByType = data.dealerId ? DETAILS_PATH_BY_TYPE : GROUP_DETAILS_PATH_BY_TYPE;
    const { path } = pathByType[data.type] ?? pathByType.DEFAULT;
    yield call(navigate, generatePath(path, { checklistId: data.id }));
  } catch (error) {
    yield put(dashboardActions.duplicateChecklistFailure(error as ApiError));
  }
}

function* fetchSelectedChecklists({ payload, meta }: FetchSelectedChecklistsRequest) {
  const { dealerId, request } = payload;

  try {
    const { data } = (yield call(restClient.request, request)) as AxiosResponse<DetailedChecklist[]>;

    yield put(
      dashboardActions.fetchSelectedChecklistsSuccess(
        {
          dealerId,
          content: data,
        },
        meta
      )
    );
  } catch (error) {
    yield put(dashboardActions.fetchSelectedChecklistsFailure(error as ApiError, meta));
  }
}

type ChecklistSimpleRequestConfig = SimpleRequestConfig & { effect?: typeof takeEvery };

const simpleRequests: Record<string, ChecklistSimpleRequestConfig> = {
  [actionTypes.PATCH_ADVICE_STATE]: {
    notifications: {
      success: {
        id: 'page.config.dealer.checklists.notification.updateAdviceState.success',
        defaultMessage: 'The item state has been updated.',
      },
      failure: {
        id: 'page.config.dealer.checklists.notification.updateAdviceState.failure',
        defaultMessage: 'An error occurred while updating the item state. Please try again later.',
      },
    },
  },
  [actionTypes.FETCH_CHECKLIST_ADVICE_HISTORY]: {
    effect: takeEvery,
    callbacks: {
      success: (content, meta, { payload: { checklistId, adviceId } }) =>
        fetchChecklistAdviceHistorySuccess(checklistId, adviceId, content, meta),
    },
  },
};

function* refreshAdviceHistory({ meta }: Action<unknown> & { meta: { checklistId: string; adviceId: string } }) {
  const { checklistId, adviceId } = meta;
  yield put(fetchChecklistAdviceHistory(checklistId, adviceId));
}

export default function* checklistSagas() {
  yield all(simpleRequestsLoader(simpleRequests));

  yield scopeToPage(CHECKLIST_DETAILED_PATHS, onDetailsPageWorker);
  yield takeLatest(actionTypes.PATCH_ADVICE, patchAdvice);
  yield takeLatest(actionTypes.UPDATE_REQUEST, updateChecklistSaga);
  yield takeLatest(actionTypes.CREATE_REQUEST, createChecklistSaga);

  yield takeEvery(actionTypes.FETCH_REQUEST, fetchChecklist);
  yield takeLatest(actionTypes.DELETE_CHECKLIST_REQUEST, deleteChecklist);
  yield takeEvery(actionTypes.SEARCH_CHECKLISTS_REQUEST, searchChecklists);
  yield takeLatest(actionTypes.DUPLICATE_CHECKLIST_REQUEST, duplicateChecklist);
  yield takeEvery(actionTypes.FETCH_CHECKLISTS_SELECT_REQUEST, fetchChecklistsSelect);
  yield takeLatest(actionTypes.FETCH_DEFAULT_CHECKLIST_REQUEST, fetchDefaultChecklist);
  yield takeLatest([LOCATION_CHANGE, FORCE_PAGE_RELOAD], startDetailsApplication);
  yield takeLatest(actionTypes.CREATE_CHECKLIST_FROM_DEFAULT_CHECKLIST, createChecklistFromDefault);
  yield takeLatest(actionTypes.FETCH_SELECTED_CHECKLISTS_REQUEST, fetchSelectedChecklists);

  // Extra cases
  yield takeLatest(FETCH_MODEL_SUCCESS, fetchExtraCollection);

  // History
  yield takeLatest(actionTypes.PATCH_ADVICE_STATE_SUCCESS, refreshAdviceHistory);
}
